import { ITextVertex } from "../../annotator/interfaces/textLayer";

// Tolerances are expressed in token heights (≈ the font size) so they stay
// correct at every zoom level. Line spacing is not: it varies far too much
// between documents to be a constant, so lines are only ever joined when the
// page itself shows nothing was skipped between them.
const SAME_LINE_VERTICAL_OVERLAP = 0.5;
const SAME_LINE_MAX_HORIZONTAL_GAP = 1;
const MAX_LINE_BREAK_VERTICAL_GAP = 3;
const MIN_TOKEN_HEIGHT = 1;

type Bounds = {
  dataI: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
  center: number;
  height: number;
};

const pageBoundsCache = new WeakMap<Array<ITextVertex>, Array<Bounds>>();

const toBounds = (token: ITextVertex): Bounds => ({
  dataI: token.dataI,
  left: token.left,
  right: token.left + token.width,
  top: token.top,
  bottom: token.top + token.height,
  center: token.top + token.height / 2,
  height: Math.max(MIN_TOKEN_HEIGHT, token.height),
});

const getPageBounds = (pageTokens: Array<ITextVertex>): Array<Bounds> => {
  const cached = pageBoundsCache.get(pageTokens);
  if (cached) {
    return cached;
  }

  const bounds = pageTokens.map(toBounds).sort((a, b) => a.center - b.center);
  pageBoundsCache.set(pageTokens, bounds);

  return bounds;
};

const firstIndexBelow = (pageBounds: Array<Bounds>, value: number): number => {
  let low = 0;
  let high = pageBounds.length;

  while (low < high) {
    const middle = (low + high) >> 1;
    if (pageBounds[middle].center <= value) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  return low;
};

const overlaps = (a: Bounds, b: Bounds): boolean => {
  return Math.min(a.right, b.right) - Math.max(a.left, b.left) > 0;
};

// True when a line the user did not select sits between the two tokens, which
// is what tells a wrapped value apart from two values on separate lines.
const isLineSkipped = (
  upper: Bounds,
  lower: Bounds,
  pageBounds: Array<Bounds>,
  selected: Set<number>,
): boolean => {
  const shared: Bounds = {
    ...upper,
    left: Math.max(upper.left, lower.left),
    right: Math.min(upper.right, lower.right),
  };

  for (
    let i = firstIndexBelow(pageBounds, upper.bottom);
    i < pageBounds.length;
    i++
  ) {
    const candidate = pageBounds[i];
    if (candidate.center >= lower.top) {
      break;
    }

    if (!selected.has(candidate.dataI) && overlaps(candidate, shared)) {
      return true;
    }
  }

  return false;
};

const areAdjacent = (
  a: Bounds,
  b: Bounds,
  pageBounds: Array<Bounds>,
  selected: Set<number>,
): boolean => {
  const height = Math.min(a.height, b.height);
  const verticalOverlap = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);

  if (verticalOverlap >= SAME_LINE_VERTICAL_OVERLAP * height) {
    const horizontalGap = Math.max(a.left, b.left) - Math.min(a.right, b.right);
    return horizontalGap <= SAME_LINE_MAX_HORIZONTAL_GAP * height;
  }

  if (
    !overlaps(a, b) ||
    -verticalOverlap > MAX_LINE_BREAK_VERTICAL_GAP * height
  ) {
    return false;
  }

  const [upper, lower] = a.top <= b.top ? [a, b] : [b, a];

  return !isLineSkipped(upper, lower, pageBounds, selected);
};

export const groupItems = (
  tokens: Array<ITextVertex>,
  pageTokens: Array<ITextVertex>,
): ITextVertex[][] => {
  const bounds = tokens.map(toBounds);
  const pageBounds = getPageBounds(pageTokens);
  const selected = new Set(tokens.map((token) => token.dataI));
  const parents = tokens.map((_, index) => index);

  const find = (index: number): number => {
    let root = index;
    while (parents[root] !== root) {
      root = parents[root];
    }

    let node = index;
    while (parents[node] !== root) {
      const next = parents[node];
      parents[node] = root;
      node = next;
    }

    return root;
  };

  const union = (a: number, b: number) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) {
      parents[Math.max(rootA, rootB)] = Math.min(rootA, rootB);
    }
  };

  for (let i = 0; i < tokens.length; i++) {
    for (let j = i + 1; j < tokens.length; j++) {
      if (
        find(i) !== find(j) &&
        areAdjacent(bounds[i], bounds[j], pageBounds, selected)
      ) {
        union(i, j);
      }
    }
  }

  const groups = new Map<number, ITextVertex[]>();

  tokens.forEach((token, index) => {
    const root = find(index);
    const group = groups.get(root);
    if (group) {
      group.push(token);
    } else {
      groups.set(root, [token]);
    }
  });

  return [...groups.values()];
};
